import type { OperationalAwardTypeCode, PoolAwardHold, PoolPositions } from '@myrewards/contracts';
import type { StatusTone } from '../ui';

export const AWARD_TYPE_LABEL: Record<OperationalAwardTypeCode, string> = {
  SDB: 'Sector Discretionary Bonus',
  EDB: 'Executive Discretionary Bonus',
  PAA: 'Professional Achievement Award',
  TAA: 'Team Achievement Award',
  SPOT: 'Spot Award',
};

/**
 * A Team Achievement Award and a Spot Award are cash only: no stock options, no election and no
 * shares (CLAUDE.md; PRD §5 for Spot). Where an options figure would be, it says so in words,
 * never a "0" that reads as options that weren't given (QA-038, APP-079).
 */
export const CASH_ONLY = 'Cash only';

/** True when this award type can't carry options or shares: shown as "Cash only". */
export const isCashOnly = (type: OperationalAwardTypeCode): boolean =>
  type === 'TAA' || type === 'SPOT';

/** Short names for tables ("Sector Discretionary", "PAA"). */
export const AWARD_TYPE_SHORT: Record<OperationalAwardTypeCode, string> = {
  SDB: 'Sector Discretionary',
  EDB: 'Executive Discretionary',
  PAA: 'PAA',
  TAA: 'TAA',
  SPOT: 'Spot Award',
};

/**
 * The plain stages people see (CLAUDE.md "Plain stages only"), one name for every award type.
 * APP-252: a Spot award's letter is the Stock Admin team's to check and release (APP-244), so its
 * stage no longer reads "With the Spot Committee" (APP-110).
 */
export const STAGE: Record<PoolAwardHold['stage'], { label: string; tone: StatusTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  approval: { label: 'Approval', tone: 'waiting' },
  with_stock_admin: { label: 'With the Stock Admin team', tone: 'info' },
  employee_choice: { label: 'Employee’s choice', tone: 'waiting' },
  paid: { label: 'Paid', tone: 'done' },
  closed: { label: 'Paid', tone: 'done' },
};

export type PoolPosition = PoolPositions['pools'][number];

const NUMBER_WORDS = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
] as const;

/** "two", "eleven" → "11": words for small counts in running copy. */
export function countWord(count: number): string {
  return NUMBER_WORDS[count] ?? String(count);
}

/** "Good morning" / "Good afternoon" / "Good evening" for the person's own clock. */
export function greeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
